// Group Chat Log — log the SAME interaction against many CRM contacts at once.
// Perfect for group DMs, group meetings, or shoot-day briefs where multiple
// people were involved.
import React, { useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { X as XIcon, Users, MessageCircle, Send } from "lucide-react";

const KINDS = [
  { v: "group_chat", label: "Group chat" },
  { v: "dm", label: "DM" },
  { v: "email", label: "Email" },
  { v: "call", label: "Call" },
  { v: "meeting", label: "Meeting" },
  { v: "note", label: "Note" },
];
const DIRECTIONS = [
  { v: "inbound", label: "Inbound" },
  { v: "outbound", label: "Outbound" },
  { v: "internal", label: "Internal / notes" },
];
const OUTCOMES = [
  { v: "positive", label: "Positive" },
  { v: "neutral", label: "Neutral" },
  { v: "negative", label: "Negative" },
];

export default function GroupChatLogModal({ contacts = [], onClose, onSaved }) {
  const [selected, setSelected] = useState([]); // array of crm ids
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("group_chat");
  const [platform, setPlatform] = useState("Telegram");
  const [direction, setDirection] = useState("inbound");
  const [outcome, setOutcome] = useState("neutral");
  const [summary, setSummary] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [followUpDate, setFollowUpDate] = useState("");
  const [saving, setSaving] = useState(false);

  const contactLabel = (c) => c.stage_name || c.legal_name || c.email || c.id;

  const available = useMemo(() => {
    const q2 = q.trim().toLowerCase();
    let list = contacts.filter(c => !selected.includes(c.id));
    if (q2) {
      list = list.filter(c => {
        const bag = `${contactLabel(c)} ${c.email || ""} ${c.twitter || ""} ${c.onlyfans || ""} ${c.justforfans || ""}`.toLowerCase();
        return bag.includes(q2);
      });
    }
    return list.slice(0, 200);
  }, [contacts, selected, q]);

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const pasteConversationHint = () => {
    // Try to auto-extract @handles from pasted conversation and preselect matching CRM contacts
    if (!note.trim()) return;
    const handles = [...note.matchAll(/@([A-Za-z0-9_]{2,})/g)].map(m => m[1].toLowerCase());
    if (!handles.length) return toast.info("No @handles detected in note");
    const matched = contacts.filter(c => {
      const h = [c.twitter, c.instagram, c.onlyfans, c.justforfans]
        .filter(Boolean).map(x => x.replace(/^@/, "").toLowerCase());
      return handles.some(hn => h.includes(hn));
    });
    if (!matched.length) return toast.info("Found handles but none match CRM");
    const ids = matched.map(m => m.id);
    const next = Array.from(new Set([...selected, ...ids]));
    setSelected(next);
    toast.success(`+${ids.length} auto-selected from @handles`);
  };

  const submit = async () => {
    if (selected.length === 0) return toast.error("Select at least one contact");
    if (!summary.trim() && !note.trim()) return toast.error("Add a summary or notes");
    if (!window.confirm(`Log this interaction on ${selected.length} contact${selected.length === 1 ? "" : "s"}?`)) return;
    setSaving(true);
    try {
      const r = await api.post("/api/crm/interactions/bulk", {
        crm_ids: selected,
        follow_up: {
          kind, platform, direction, outcome,
          summary: summary.trim(),
          note: note.trim(),
          date, follow_up_date: followUpDate || null,
        },
      });
      toast.success(`✓ Logged on ${r.data.logged} contact${r.data.logged === 1 ? "" : "s"}`);
      onSaved && onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk log failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-xl z-50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose} data-testid="crm-group-log-modal">
      <div className="bg-black/95 border border-purple-500/30 max-w-3xl w-full my-8 rounded-xl flex flex-col max-h-[calc(100vh-4rem)] shadow-2xl shadow-purple-500/20"
        onClick={(e) => e.stopPropagation()}>
        {/* Header - Pink/Purple Neon Theme */}
        <div className="p-6 border-b border-purple-500/20 flex items-center justify-between shrink-0 bg-gradient-to-r from-purple-900/20 to-pink-900/20">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-pink-400 inline-flex items-center gap-2 mb-2">
              <Users size={14} /> GROUP INTERACTION LOG
            </div>
            <h2 className="font-heading text-2xl bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Log to multiple contacts
            </h2>
            <div className="text-sm mt-2 text-zinc-400">
              Same note gets pushed to every selected contact's timeline.
            </div>
          </div>
          <button onClick={onClose} data-testid="crm-group-log-close" 
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-all">
            <XIcon size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5 overflow-y-auto flex-1">

          {/* Contact picker - Enhanced Theme */}
          <div className="rounded-xl border border-purple-500/20 p-4 space-y-3 bg-gradient-to-br from-purple-900/10 to-pink-900/10"
            data-testid="crm-group-picker">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wider text-purple-400">
                Contacts · {selected.length} selected
              </div>
              {selected.length > 0 && (
                <button onClick={() => setSelected([])}
                  data-testid="crm-group-clear"
                  className="text-xs px-3 py-1 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 text-red-400 rounded-lg transition-all">
                  Clear
                </button>
              )}
            </div>
            {selected.length > 0 && (
              <div className="flex flex-wrap gap-1" data-testid="crm-group-chips">
                {selected.map(id => {
                  const c = contacts.find(x => x.id === id);
                  return (
                    <span key={id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]"
                      style={{ background: "var(--gio-primary)", color: "#000" }}
                      data-testid={`crm-group-chip-${id}`}>
                      {c ? contactLabel(c) : id}
                      <button onClick={() => toggle(id)} className="opacity-80 hover:opacity-100"
                        data-testid={`crm-group-chip-remove-${id}`}>×</button>
                    </span>
                  );
                })}
              </div>
            )}
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search & add contacts…"
              className="gio-input text-[12px]"
              data-testid="crm-group-search" />
            {q && available.length > 0 && (
              <div className="max-h-[180px] overflow-y-auto border rounded" style={{ borderColor: "var(--gio-border)" }}>
                {available.map(c => (
                  <button key={c.id} onClick={() => { toggle(c.id); setQ(""); }}
                    data-testid={`crm-group-add-${c.id}`}
                    className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-[color:var(--gio-primary-soft)] border-b"
                    style={{ borderColor: "var(--gio-border)" }}>
                    <span style={{ color: "var(--gio-text)" }}>{contactLabel(c)}</span>
                    {c.type && (
                      <span className="ml-2 text-[9px] uppercase tracking-wider" style={{ color: "var(--gio-muted-3)" }}>
                        {c.type}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            {q && available.length === 0 && (
              <div className="text-[10px] italic" style={{ color: "var(--gio-muted-3)" }}>No matches</div>
            )}
          </div>

          {/* Meta row 1 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Field label="Kind">
              <select value={kind} onChange={(e) => setKind(e.target.value)}
                className="gio-input text-[12px]" data-testid="crm-group-kind">
                {KINDS.map(k => <option key={k.v} value={k.v}>{k.label}</option>)}
              </select>
            </Field>
            <Field label="Platform">
              <input value={platform} onChange={(e) => setPlatform(e.target.value)}
                placeholder="Telegram, WhatsApp, X…"
                className="gio-input text-[12px]" data-testid="crm-group-platform" />
            </Field>
            <Field label="Direction">
              <select value={direction} onChange={(e) => setDirection(e.target.value)}
                className="gio-input text-[12px]" data-testid="crm-group-direction">
                {DIRECTIONS.map(d => <option key={d.v} value={d.v}>{d.label}</option>)}
              </select>
            </Field>
            <Field label="Outcome">
              <select value={outcome} onChange={(e) => setOutcome(e.target.value)}
                className="gio-input text-[12px]" data-testid="crm-group-outcome">
                {OUTCOMES.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </Field>
          </div>

          {/* Meta row 2 */}
          <div className="grid grid-cols-2 gap-2">
            <Field label="Date">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="gio-input text-[12px]" data-testid="crm-group-date" />
            </Field>
            <Field label="Follow-up date (optional)">
              <input type="date" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)}
                className="gio-input text-[12px]" data-testid="crm-group-followup" />
            </Field>
          </div>

          {/* Summary + note */}
          <Field label="Summary / Subject *">
            <input value={summary} onChange={(e) => setSummary(e.target.value)}
              placeholder="e.g. Athens shoot planning group chat"
              className="gio-input text-[12px]" data-testid="crm-group-summary" />
          </Field>
          <Field label="Full note / paste conversation">
            <textarea value={note} onChange={(e) => setNote(e.target.value)}
              rows={6}
              placeholder="Paste the group chat here — @handles will be detected for auto-selection."
              className="gio-input text-[12px]" data-testid="crm-group-note" />
            {note.trim() && (
              <div className="mt-2 flex gap-2">
                <button onClick={pasteConversationHint}
                  data-testid="crm-group-detect-handles"
                  className="text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 text-purple-300 rounded-lg transition-all">
                  <MessageCircle size={12} /> Detect @handles
                </button>
                
                {note.length > 100 && (
                  <button
                    onClick={async () => {
                      toast.info('AI is generating summary...');
                      try {
                        const res = await api.post('/api/ai/summarize', { text: note });
                        setSummary(res.data.summary || '');
                        toast.success('Summary generated!');
                      } catch (err) {
                        toast.error('Failed to generate summary');
                      }
                    }}
                    className="text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white font-semibold rounded-lg transition-all shadow-lg shadow-indigo-500/30">
                    ✨ AI Summarize
                  </button>
                )}
              </div>
            )}
          </Field>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-zinc-900 flex gap-3 shrink-0">
          <button onClick={submit}
            disabled={saving || selected.length === 0 || (!summary.trim() && !note.trim())}
            data-testid="crm-group-log-submit"
            className="btn-primary flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            <Send size={13} />
            {saving ? "Logging…" : `Log on ${selected.length} contact${selected.length === 1 ? "" : "s"}`}
          </button>
          <button onClick={onClose} data-testid="crm-group-log-cancel" className="btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline mb-1.5 text-[9px]" style={{ color: "var(--gio-muted-2)" }}>{label}</div>
      {children}
    </label>
  );
}
